import { FC } from 'react'
import { ReactComponent as BackIcon } from 'src/assets/svg/arrow-left-s-line.svg'
import { CampaignDetailView } from 'src/pages/ThemeDetail/CampaignDetailHeader'
import { cn } from 'src/utils/cn'
import styles from './contentAnalysisHeader.module.scss'

// What a screen can ask of the header when it renders one for itself.
export interface ContentHeaderOptions {
  embedded?: boolean
  onBack?: () => void
  trail?: string
}

interface ContentAnalysisHeaderProps {
  // Inside a card rather than on the page background: it fills the card's own
  // width instead of centring a column of its own.
  embedded?: boolean
  // Where the back arrow goes. Passing it turns the header into the trail the
  // comparison screens use — a way back, not a page title — since on those the
  // heading belongs to the report under it.
  onBack?: () => void
  onViewChange: (view: CampaignDetailView) => void
  // What the trail names after the page, e.g. "Comparisons".
  trail?: string
  view: CampaignDetailView
}

// The switch reads "Overview / Comparisons" on every feature that carries it,
// so the three are recognisably the same control rather than three similar
// ones.
const COPY = {
  comparison: 'Comparisons',
  general: 'Overview',
  title: 'Content Analysis',
}

const VIEWS: { label: string; value: CampaignDetailView }[] = [
  { label: COPY.general, value: 'general' },
  { label: COPY.comparison, value: 'comparison' },
]

/*
 * Content Analysis' title and the switcher between its two views.
 *
 * Deliberately its own component rather than Review Analysis' bent around a
 * second use: the two are the same shape today, and a shared one would have to
 * take the page's name, its trail's name and its back behaviour as props —
 * which is the whole component. If a third arrives, this is the one to merge.
 */
export const ContentAnalysisHeader: FC<ContentAnalysisHeaderProps> = ({
  embedded = false,
  onBack,
  onViewChange,
  trail,
  view,
}) => (
  <div className={cn(styles.header, embedded && styles.headerEmbedded)}>
    {/* The overview is where the feature starts, so it has nowhere to go back
        to and names itself instead of offering a way out of a page it is. */}
    {onBack ? (
      <button className={styles.back} onClick={onBack} type="button">
        <BackIcon className={styles.backIcon} />
        <span className={styles.backLabel}>
          {COPY.title}
          {trail && <span className={styles.trail}> · {trail}</span>}
        </span>
      </button>
    ) : (
      <span className={styles.backLabel}>{COPY.title}</span>
    )}

    <div className={styles.viewToggle}>
      {VIEWS.map((option) => (
        <button
          className={cn(
            styles.viewToggleOption,
            view === option.value && styles.viewToggleOptionActive
          )}
          key={option.value}
          onClick={() => onViewChange(option.value)}
          type="button"
        >
          {option.label}
        </button>
      ))}
    </div>
  </div>
)
